import { PaperClipOutlined } from "@ant-design/icons";
import { AccelFile, FileSize } from "@axl-dev/school-sdk";
import { Button, message, Tooltip } from "antd";
import Humanize from "humanize-plus";
import { FC, useMemo } from "react";
import { useDropzone } from "react-dropzone";

import { useLoc } from "@app/shared/lib/hooks";

type Props = {
  label?: React.ReactNode;
  maxFiles?: number;
  maxFileSize?: FileSize;
  disabled: boolean;
  forbiddenFileExtensions?: string[];
  iconClassname?: string;
  btnClassName?: string;
  onAttach: (files: AccelFile[]) => void;
};

export const FileInput: FC<Props> = ({
  label,
  maxFiles,
  disabled,
  forbiddenFileExtensions,
  maxFileSize,
  iconClassname,
  btnClassName,
  onAttach,
}) => {
  const loc = useLoc();
  const dropzone = useDropzone({
    onDrop: (x) => {
      let files = x.map((_x) => AccelFile.fromFile(_x));
      if (forbiddenFileExtensions) {
        files = files.filter(
          (x) => !forbiddenFileExtensions!.includes(x.extension),
        );
      }

      if (maxFileSize) {
        const hasIgnoredItems = files.some(
          (droppedFile) => droppedFile.fileSize.bytes > maxFileSize!.bytes,
        );
        files = files.filter(
          (droppedFile) => droppedFile.fileSize.bytes <= maxFileSize!.bytes,
        );
        if (hasIgnoredItems)
          message.warning({
            content: loc.word("File.maxFileSizeTemplate", {
              default: `Max file size: {0}`,
              args: [Humanize.fileSize(maxFileSize.bytes, 0)],
            }),
          });
      }

      onAttach(files);
    },
    disabled,
  });

  const tooltipTitle = useMemo(() => {
    let tooltip = loc.word("Attach files");
    if (maxFiles)
      tooltip += ` (${loc.word("Max", { case: "lower" })}: ${maxFiles})`;

    return tooltip;
  }, [loc, maxFiles]);

  return (
    <div {...dropzone.getRootProps()}>
      <input {...dropzone.getInputProps()} />
      <Tooltip placement="topRight" title={tooltipTitle}>
        <Button
          type="link"
          className={btnClassName ?? "file-input-btn"}
          size="large"
          disabled={disabled}
          icon={<PaperClipOutlined className={iconClassname} />}
        >
          {label}
        </Button>
      </Tooltip>
    </div>
  );
};
